import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UserPlus, UserX, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEL = { width: "100%", fontSize: 12, padding: "7px 9px", border: "0.5px solid var(--border, #e5e5e5)", borderRadius: 6, background: "#fff" };
const errMsg = (e, fb) => e?.response?.data?.detail || fb;
const BTN = { fontSize: 10, padding: "3px 8px", borderRadius: 6, border: "0.5px solid var(--border, #e5e5e5)", background: "#fff", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 };
const until = (iso) => (iso ? `Until ${new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" })} ET` : "Until revoked");

export function AssignBackupModal({ token, row, current = [], onClose, onSaved }) {
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [users, setUsers] = useState([]);
  const [picked, setPicked] = useState(() => new Set());
  const [q, setQ] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const taken = new Set(current.map((b) => b.backup_id));
  useEffect(() => {
    axios.get(`${API}/admin/pc-backups/candidates`, h)
      .then((r) => setUsers((r.data.users || []).filter((u) => u.id !== row.user_id && !taken.has(u.id))))
      .catch(() => toast.error("Couldn't load agents"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const shown = users.filter((u) => `${u.full_name || ""} ${u.email || ""}`.toLowerCase().includes(q.toLowerCase()));
  const save = async () => {
    if (!picked.size) { toast.warning("Pick at least one backup agent"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/pc-backups`, { owner_id: row.user_id, backup_ids: [...picked], notify }, h);
      const n = r.data.added?.length || 0;
      toast.success(`${n} backup${n === 1 ? "" : "s"} added to ${row.name}'s campaign`);
      onSaved();
    } catch (e) { toast.error(errMsg(e, "Couldn't add the backups")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="pc-backup-modal" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>{current.length ? "Add backup" : "Assign backup"}</DialogTitle>
          <DialogDescription>Backups work this Personal Campaign from their Lead Dialer. Lead ownership stays with the owner.</DialogDescription>
        </DialogHeader>
        <div style={{ display: "grid", gap: 12, fontSize: 12 }}>
          <label>Campaign owner<input data-testid="pc-backup-owner" value={row.name} readOnly style={{ ...SEL, background: "#f6f7f9", marginTop: 4 }} /></label>
          <div>
            <div style={{ marginBottom: 4 }}>Backup agents {picked.size > 0 && <b data-testid="pc-backup-picked-count">· {picked.size} selected</b>}</div>
            <input data-testid="pc-backup-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search agents…" style={{ ...SEL, marginBottom: 6 }} />
            <div data-testid="pc-backup-agent-list" style={{ maxHeight: 200, overflowY: "auto", border: "0.5px solid var(--border, #e5e5e5)", borderRadius: 6 }}>
              {shown.length === 0 && <div style={{ padding: 10, color: "#888" }}>No agents available.</div>}
              {shown.map((u) => (
                <label key={u.id} data-testid={`pc-backup-agent-${u.id}`} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderBottom: "0.5px solid #f2f2f2", cursor: "pointer", background: picked.has(u.id) ? "#F5F3FF" : "#fff" }}>
                  <input type="checkbox" checked={picked.has(u.id)} onChange={() => toggle(u.id)} />
                  <span style={{ flex: 1 }}>{u.full_name || u.email}</span>
                  <span style={{ fontSize: 10, color: "#888" }}>{u.email}</span>
                </label>
              ))}
            </div>
          </div>
          <div data-testid="pc-backup-window-note" style={{ padding: "8px 10px", borderRadius: 6, background: "#F5F3FF", color: "#5B21B6" }}>
            {current.length ? `New backups share the current window: ${until(current[0]?.expires_at).replace("Until", "until")}.` : "Access ends at 8:00 pm Eastern today (tomorrow if it's already past 8:00 pm)."}
          </div>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <input type="checkbox" data-testid="pc-backup-notify" checked={notify} onChange={(e) => setNotify(e.target.checked)} /> Notify backup agents
          </label>
        </div>
        <DialogFooter>
          <button data-testid="pc-backup-cancel" className="btn" onClick={onClose}>Cancel</button>
          <button data-testid="pc-backup-save" className="btn btn-primary" disabled={busy} onClick={save}>{busy ? <Loader2 className="animate-spin" style={{ width: 13, height: 13 }} /> : "Save"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BackupCell({ token, row, current = [], onChanged }) {
  const [assign, setAssign] = useState(false);
  const [revoke, setRevoke] = useState(false);
  const [busy, setBusy] = useState(false);
  const names = current.map((b) => b.backup_name);
  const doRevoke = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/pc-backups/${row.user_id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Backup access for ${row.name}'s campaign revoked`); setRevoke(false); onChanged();
    } catch (e) { toast.error(errMsg(e, "Couldn't revoke")); }
    finally { setBusy(false); }
  };
  return (
    <td style={{ padding: "10px 8px" }} onClick={(e) => e.stopPropagation()}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        {current.map((b) => (
          <span key={b.backup_id} data-testid={`pc-backup-chip-${row.user_id}-${b.backup_id}`}
            style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: "#EDE9FE", color: "#5B21B6" }}>Backup: {b.backup_name}</span>
        ))}
        <button data-testid={`pc-backup-assign-${row.user_id}`} style={BTN} onClick={() => setAssign(true)}><UserPlus style={{ width: 11, height: 11 }} />{current.length ? "Add Backup" : "Assign Backup"}</button>
        {current.length > 0 && <button data-testid={`pc-backup-revoke-${row.user_id}`} style={{ ...BTN, color: "#B42318" }} onClick={() => setRevoke(true)}><UserX style={{ width: 11, height: 11 }} />Revoke Backup</button>}
      </div>
      {current.length > 0 && <div data-testid={`pc-backup-until-${row.user_id}`} style={{ fontSize: 10, color: "#6B7280", marginTop: 3 }}>{until(current[0]?.expires_at)}</div>}
      {assign && <AssignBackupModal token={token} row={row} current={current} onClose={() => setAssign(false)} onSaved={() => { setAssign(false); onChanged(); }} />}
      {revoke && (
        <Dialog open onOpenChange={(o) => !o && setRevoke(false)}>
          <DialogContent data-testid="pc-backup-revoke-dialog">
            <DialogHeader>
              <DialogTitle>Revoke backup access?</DialogTitle>
              <DialogDescription data-testid="pc-backup-revoke-names">
                {names.join(", ")} will lose access to {row.name}&apos;s Personal Campaign straight away.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <button data-testid="pc-backup-revoke-cancel" className="btn" onClick={() => setRevoke(false)}>Cancel</button>
              <button data-testid="pc-backup-revoke-confirm" className="btn btn-primary" disabled={busy} style={{ background: "#B42318", borderColor: "#B42318" }} onClick={doRevoke}>Revoke all</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </td>
  );
}

export const breakdownText = (b) => {
  const parts = Object.entries(b.outcomes || {}).sort((x, y) => y[1] - x[1]).map(([k, v]) => `${v} ${k}`);
  return `${b.backup_name} worked ${b.worked} lead${b.worked === 1 ? "" : "s"}${parts.length ? ` (${parts.join(", ")})` : ""}`;
};
